import React, { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { SANDBOX_AUTOLOGIN } from "../../config/env";
import InstitutionSelector, { AumTier } from "./InstitutionSelector";
import FilterControls from "./FilterControls";
import TableView from "./TableView";
import { DisplayFields, SortOption } from "./types";
import ModeToggle from "../../components/13FQoqHistory/ModeToggle";
import TickerSearch from "../../components/13FQoqHistory/TickerSearch";
import { Mode, useHoldingsHistory } from "../../hooks/useHoldingsHistory";
import { generateYQsFromEnv, latestEndedYQ } from "../../utils/yearQuarter";
import { RowKey, UIPair } from "../../helpers/useHoldingsHistoryHelper";
import { setLogoutHandler } from "../../auth/unauthorizedHandler";
import { fetchInstitutionsOnce } from "../../api/institutionsCache";

type InstitutionMeta = { name: string; ticker: string | null; tier: AumTier };

const HoldingsHistory: React.FC = (): ReactElement | null => {
    const { logout } = useContext(AuthContext);

    useEffect(() => {
        setLogoutHandler(() => logout);
        return () => setLogoutHandler(null);
    }, [logout]);

    const yqList = useMemo(() => generateYQsFromEnv(), []);
    const latestEnded = useMemo(() => latestEndedYQ(), []);

    const [defaultFromYQ, defaultToYQ] = useMemo(() => {
        if (!yqList.length) return ["2013Q2", "2013Q2"];
        const toIndex = yqList.includes(latestEnded) ? yqList.indexOf(latestEnded) : Math.max(0, yqList.length - 1);
        const fromIndex = Math.max(0, toIndex - 2);
        return [yqList[fromIndex], yqList[toIndex]];
    }, [yqList, latestEnded]);

    const [mode, setMode] = useState<Mode>("institution");

    const [ticker, setTicker] = useState("");
    const [inputTicker, setInputTicker] = useState("");
    const [tickerQueried, setTickerQueried] = useState(false);

    const [aggregatePositions, setAggregatePositions] = useState(false);
    const [tickerFilter, setTickerFilter] = useState("");
    const [sortOption, setSortOption] = useState<SortOption>("value");
    const [showPositionsInNewestQuarter, setShowPositionsInNewestQuarter] = useState(true);
    const [displayFields, setDisplayFields] = useState<DisplayFields>({
        valueDelta: true,
        valuePercent: true,
        amountDelta: true,
        amountPercent: true,
    });
    const [showPositionShare, setShowPositionShare] = useState(true);

    // start with 0 so the selector can auto-select the first tier-sorted item
    const [institutionId, setInstitutionId] = useState<number>(0);

    const [showPositionTypes, setShowPositionTypes] = useState<{ [type: string]: boolean }>({
        SHARE: true,
        CALL: true,
        PUT: true,
    });

    const [pageError, setPageError] = useState<string | null>(null);
    const [tickerError, setTickerError] = useState<string | null>(null);

    const [pendingFromYQ, setPendingFromYQ] = useState<string>(defaultFromYQ);
    const [pendingToYQ, setPendingToYQ] = useState<string>(defaultToYQ);

    const [appliedFromYQ, setAppliedFromYQ] = useState<string | null>(null);
    const [appliedToYQ, setAppliedToYQ] = useState<string | null>(null);
    const [appliedInstitutionId, setAppliedInstitutionId] = useState<number | null>(null);
    const [appliedShowPositionTypes, setAppliedShowPositionTypes] = useState(showPositionTypes);

    // meta map to render pills in ticker view
    const [institutionMetaMap, setInstitutionMetaMap] = useState<Record<number, InstitutionMeta>>({});

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const list = await fetchInstitutionsOnce();
                if (!mounted || !Array.isArray(list)) return;
                const map: Record<number, InstitutionMeta> = {};
                for (const i of list as any[]) {
                    map[i.id] = {
                        name: i.name,
                        ticker: i.ticker ?? null,
                        tier: i.form13FAumTierEnum as AumTier,
                    };
                }
                setInstitutionMetaMap(map);
            } catch {
                // non-fatal; pills just won't show if this fails
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const typeFromRowKey = (rk: string): string => (rk?.split("|")[1] || "").toUpperCase();
    const hasFetched = appliedFromYQ !== null && appliedToYQ !== null && appliedInstitutionId !== null;

    const effectiveFromYQ = appliedFromYQ ?? pendingFromYQ;
    const effectiveToYQ = appliedToYQ ?? pendingToYQ;

    const effectiveFromYear = parseInt(effectiveFromYQ.slice(0, 4), 10);
    const effectiveToYear = parseInt(effectiveToYQ.slice(0, 4), 10);

    const enabled = mode === "ticker" ? tickerQueried : hasFetched;

    const applyPendingPeriod = () => {
        setAppliedFromYQ(pendingFromYQ);
        setAppliedToYQ(pendingToYQ);
        setAppliedInstitutionId(institutionId);
        setAppliedShowPositionTypes(showPositionTypes);
        setPageError(null);
    };

    const onFetch = () => {
        if (mode === "ticker" && !tickerQueried && inputTicker.trim()) {
            setTicker(inputTicker.trim().toUpperCase());
            setTickerQueried(true);
        }
        applyPendingPeriod();
    };

    const state = useHoldingsHistory({
        mode,
        institutionId: mode === "institution" ? (appliedInstitutionId ?? institutionId) : institutionId,
        ticker,
        tickerQueried,
        fromYearQuarter: effectiveFromYQ,
        toYearQuarter: effectiveToYQ,
        fromYear: effectiveFromYear,
        toYear: effectiveToYear,
        showPositionTypes: mode === "institution" ? appliedShowPositionTypes : showPositionTypes,
        enabled,
    });

    const loading = state.loading;
    const computedPageError = state.pageError ?? pageError;
    const computedTickerError = state.tickerError ?? tickerError;

    const allYearQuarters = state.allYearQuarters;

    const { pairsForUI, pivotForUI } = useMemo(() => {
        if (mode === "ticker") {
            if (aggregatePositions && state.pivotTickerAgg && state.pairsTickerAgg) {
                return {
                    pairsForUI: (state.sortedPairsTickerAgg ?? state.pairsTickerAgg) as UIPair[],
                    pivotForUI: state.pivotTickerAgg as Record<RowKey, Record<string, any>>,
                };
            }
            if (state.pivotTicker && state.pairsTicker) {
                return {
                    pairsForUI: (state.sortedPairsTicker ?? state.pairsTicker) as UIPair[],
                    pivotForUI: state.pivotTicker as Record<RowKey, Record<string, any>>,
                };
            }
        }
        return { pairsForUI: [] as UIPair[], pivotForUI: {} as Record<RowKey, Record<string, any>> };
    }, [
        mode,
        aggregatePositions,
        state.pivotTicker,
        state.pivotTickerAgg,
        state.pairsTicker,
        state.pairsTickerAgg,
        state.sortedPairsTicker,
        state.sortedPairsTickerAgg,
    ]);

    const filteredPairsTicker = useMemo(() => {
        if (mode !== "ticker" || !pairsForUI.length) return [];
        const targetYQ = state.newestYQWithData;
        const base = pairsForUI;

        return base.filter(({ rowKey }) => {
            const rowType = typeFromRowKey(rowKey);
            if (showPositionTypes && !showPositionTypes[rowType]) return false;

            if (!state.pivotTicker && !state.pivotTickerAgg) return true;

            if (!targetYQ || !showPositionsInNewestQuarter) {
                return Object.keys(pivotForUI[rowKey] ?? {}).length > 0;
            }

            const current = pivotForUI[rowKey]?.[targetYQ];
            const wasExited = Object.values(pivotForUI[rowKey] ?? {}).some((p: any) => p?.isExited);
            return !!current || wasExited;
        });
    }, [
        mode,
        pairsForUI,
        pivotForUI,
        state.newestYQWithData,
        showPositionsInNewestQuarter,
        state.pivotTicker,
        state.pivotTickerAgg,
        showPositionTypes,
    ]);

    const filteredPairsInstitution = useMemo(() => {
        if (mode !== "institution" || !state.pivotInstitution) return [];
        const targetYQ = state.newestYQWithData;

        const arr: { ticker: string; type: string; rowKey: string }[] = [];
        Object.keys(state.pivotInstitution).forEach((t) =>
            Object.keys(state.pivotInstitution![t]).forEach((type) => arr.push({ ticker: t, type, rowKey: `${t}|${type}|0` }))
        );

        const base =
            sortOption === "ticker"
                ? [...arr].sort((a, b) => a.ticker.localeCompare(b.ticker) || a.type.localeCompare(b.type))
                : [...arr].sort((a, b) => {
                    const val = (t: string, ty: string) => state.pivotInstitution![t]?.[ty]?.[state.newestYQWithData!]?.value ?? 0;
                    return val(b.ticker, b.type) - val(a.ticker, a.type);
                });

        return base.filter(({ ticker, type }) => {
            if (showPositionTypes && showPositionTypes[type] === false) return false;
            if (tickerFilter && !ticker.toLowerCase().includes(tickerFilter.toLowerCase())) return false;

            if (!targetYQ || !showPositionsInNewestQuarter) {
                return Object.keys(state.pivotInstitution![ticker]?.[type] ?? {}).length > 0;
            }
            const current = state.pivotInstitution![ticker]?.[type]?.[targetYQ];
            const wasExited = Object.values(state.pivotInstitution![ticker]?.[type] ?? {}).some((p: any) => p?.isExited);
            return !!current || wasExited;
        });
    }, [mode, state.pivotInstitution, state.newestYQWithData, sortOption, tickerFilter, showPositionsInNewestQuarter, showPositionTypes]);

    const totalsByYQ = useMemo(() => {
        const totals: Record<string, number> = {};
        for (const yq of allYearQuarters) totals[yq] = 0;

        if (mode === "ticker" && pivotForUI) {
            for (const { rowKey } of filteredPairsTicker) {
                for (const yq of allYearQuarters) {
                    const v = pivotForUI[rowKey]?.[yq]?.value ?? 0;
                    totals[yq] = (totals[yq] ?? 0) + v;
                }
            }
        } else if (mode === "institution" && state.pivotInstitution) {
            for (const { ticker, type } of filteredPairsInstitution) {
                for (const yq of allYearQuarters) {
                    const v = state.pivotInstitution[ticker]?.[type]?.[yq]?.value ?? 0;
                    totals[yq] = (totals[yq] ?? 0) + v;
                }
            }
        }
        return totals;
    }, [mode, filteredPairsTicker, filteredPairsInstitution, pivotForUI, state.pivotInstitution, allYearQuarters]);

    const totalValueInNewestQuarter = state.totalValueInNewestQuarter;
    const showTable = mode === "ticker" ? tickerQueried : hasFetched;
    const { canLoadMore, loadingMore, loadMore } = state;

    useEffect(() => {
        const onScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
            const distance = scrollHeight - (scrollTop + clientHeight);
            if (distance < 1200 && canLoadMore && !loadingMore) loadMore();
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [canLoadMore, loadingMore, loadMore]);

    return (
        <div style={{ backgroundColor: "#1E1E1E", color: "#EAEAEA", padding: 16, minHeight: "100vh" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <ModeToggle
                    mode={mode}
                    setMode={(m) => {
                        setMode(m);
                        if (m === "ticker") {
                            setTicker((prev) => prev || "NVDA");
                            setInputTicker((prev) => prev || "NVDA");
                            setTickerError(null);
                            setTickerFilter("");
                        } else {
                            setTickerError(null);
                        }
                        setPageError(null);
                    }}
                    onResetErrors={() => {
                        setTickerError(null);
                        setPageError(null);
                    }}
                />
                {!SANDBOX_AUTOLOGIN && (
                    <button
                        onClick={logout}
                        style={{ padding: "8px 16px", backgroundColor: "#222", color: "#fff", border: "1px solid #666", borderRadius: 4, cursor: "pointer" }}
                    >
                        Log out
                    </button>
                )}
            </div>

            {computedPageError && (
                <div style={{ marginBottom: "1rem", padding: "8px 12px", backgroundColor: "#3b1e1e", border: "1px solid #a44", borderRadius: 4, color: "#ffbcbc" }}>
                    {computedPageError}
                </div>
            )}

            {mode === "ticker" ? (
                <TickerSearch
                    inputTicker={inputTicker}
                    setInputTicker={setInputTicker}
                    setTicker={setTicker}
                    setTickerQueried={setTickerQueried}
                    tickerError={computedTickerError}
                    setTickerError={setTickerError}
                    setPageError={setPageError}
                    onSearchApplyPeriod={applyPendingPeriod}
                />
            ) : (
                <InstitutionSelector
                    selectedId={institutionId}
                    onChange={(id) => {
                        setInstitutionId(id);
                        setPageError(null);
                    }}
                />
            )}

            <FilterControls
                mode={mode}
                tickerFilter={mode === "institution" ? tickerFilter : ""}
                setTickerFilter={setTickerFilter}
                showTickerFilter={mode === "institution"}
                sortOption={sortOption}
                setSortOption={setSortOption}
                showPositionsInNewestQuarter={showPositionsInNewestQuarter}
                setShowPositionsInNewestQuarter={setShowPositionsInNewestQuarter}
                showPositionShare={showPositionShare}
                setShowPositionShare={setShowPositionShare}
                displayFields={displayFields}
                setDisplayFields={setDisplayFields}
                allYearQuarters={yqList}
                pendingFromYQ={pendingFromYQ}
                pendingToYQ={pendingToYQ}
                setPendingFromYQ={setPendingFromYQ}
                setPendingToYQ={setPendingToYQ}
                showPositionTypes={showPositionTypes}
                setShowPositionTypes={setShowPositionTypes}
                aggregatePositions={aggregatePositions}
                setAggregatePositions={setAggregatePositions}
                onFetch={onFetch}
                fetchDisabled={loading}
                hideFetch={mode === "ticker"}
            />

            {showTable ? (
                mode === "ticker" ? (
                    <TableView
                        allYearQuarters={allYearQuarters}
                        pairs={filteredPairsTicker as UIPair[]}
                        rowPivot={pivotForUI as any}
                        totalsByYQ={totalsByYQ}
                        totalValueInNewestQuarter={totalValueInNewestQuarter}
                        showPositionShare={showPositionShare}
                        displayFields={displayFields}
                        institutionMap={state.institutionMap}
                        institutionMetaMap={institutionMetaMap}
                        mode={mode}
                        aggregatePositions={aggregatePositions}
                        columnMetaByPeriod={state.columnMetaByPeriod}
                    />
                ) : (
                    <TableView
                        allYearQuarters={allYearQuarters}
                        pairs={filteredPairsInstitution.map((p) => ({ ...p, institutionId: 0, rowKey: p.rowKey })) as any}
                        rowPivot={Object.fromEntries(
                            filteredPairsInstitution.map((p) => [p.rowKey, state.pivotInstitution![p.ticker][p.type]])
                        ) as any}
                        totalsByYQ={totalsByYQ}
                        totalValueInNewestQuarter={totalValueInNewestQuarter}
                        showPositionShare={showPositionShare}
                        displayFields={displayFields}
                        institutionMap={state.institutionMap}
                        institutionMetaMap={institutionMetaMap}
                        mode={mode}
                        aggregatePositions={false}
                        columnMetaByPeriod={state.columnMetaByPeriod}
                    />
                )
            ) : (
                !computedTickerError && (
                    <div style={{ marginTop: 8, opacity: 0.8 }}>
                        {mode === "ticker" ? (
                            <>Enter a ticker and press <strong>Search</strong> to load results.</>
                        ) : (
                            <>Pick a period range and press <strong>Fetch</strong> to load results.</>
                        )}
                    </div>
                )
            )}

            {mode === "ticker" && tickerQueried && !loading && state.data.every((q) => q.positions.length === 0) && !computedTickerError && (
                <div style={{ marginTop: 12, opacity: 0.9 }}>
                    No positions found for <strong>{ticker}</strong> in the selected period range.
                </div>
            )}

            {mode === "institution" && hasFetched && !loading && state.data.every((q) => q.positions.length === 0) && (
                <div style={{ marginTop: 12, opacity: 0.9 }}>
                    No positions found for the selected period range.
                </div>
            )}

            {loading && <div style={{ marginTop: 12, opacity: 0.8 }}>Loading…</div>}
            {loadingMore && <div style={{ marginTop: 12, opacity: 0.8 }}>Loading more…</div>}
        </div>
    );
};

export default HoldingsHistory;
